"use client"
import { useMemo, useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { categorySlugForProduct, defaultVariantColor } from '../../src/lib/products'
import { deriveBacking, buildProductMetaDescription, titleWithVariant, formatProductSize } from '../../src/lib/seo'
import { Copy, Star } from 'lucide-react'
import dynamic from 'next/dynamic'
import site from '../../src/data/site.json'
const APlusContent = dynamic(() => import('./APlusContent'))

type Product = any

export default function ProductDetailClient({ product, related, suggestions = [], nonce }: { product: Product, related: Product[]; suggestions?: Product[]; nonce?: string }) {
  const variantOptions = product?.variants?.options || []
  const defaultId = product?.variants?.defaultId || (variantOptions[0]?.id ?? null)
  const [activeVariantId, setActiveVariantId] = useState<string | null>(defaultId)
  const activeVariant = useMemo(() => variantOptions.find((o: any) => o.id === activeVariantId) || variantOptions[0] || null, [variantOptions, activeVariantId])
  const displayHeading = product?.slug === 'rabbit-fur-runner' ? 'Faux Rabbit Fur Runner Rug for Bedroom' : product.title
  const activeSalePrice = typeof activeVariant?.salePrice === 'number' ? activeVariant.salePrice : (product?.salePrice ?? product?.price)
  const activeListPrice = typeof activeVariant?.listPrice === 'number' ? activeVariant.listPrice : product?.listPrice

  const gallery: { src: string; caption?: string }[] = useMemo(() => {
    const imgs = activeVariant?.gallery?.length ? activeVariant.gallery : (product.images?.length ? product.images : (product.cover ? [product.cover] : []))
    return (imgs || []).map((src: string, i: number) => ({ src, caption: `${product.title} ${i + 1}` }))
  }, [activeVariant?.id, product.images, product.cover, product.title])

  const [heroIndex, setHeroIndex] = useState(0)
  // Touch swipe state for mobile carousel feel
  const heroRef = useRef<HTMLDivElement | null>(null)
  const startXRef = useRef(0)
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [animating, setAnimating] = useState(false)
  const [slideW, setSlideW] = useState(0)
  const didDragRef = useRef(false)
  useEffect(() => {
    const update = () => setSlideW(heroRef.current?.clientWidth || 0)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  useEffect(() => { setAnimating(true); setDragX(0) }, [heroIndex])
  useEffect(() => setHeroIndex(0), [activeVariantId])

  const dims = product?.specifications?.dimensions || product?.specifications?.runner || product?.specifications?.bathMat || ''
  const runnerSize = product?.specifications?.runner || ''
  const bathMatSize = product?.specifications?.bathMat || ''
  const baseAlt = product?.title || ''
  const heroAlt = [baseAlt, activeVariant?.label, dims].filter(Boolean).join(' — ')
  const backing = deriveBacking(product)
  // Try to parse size parts from title e.g. "(24x55 + 16x24 in)"
  const titleSizeParts: string[] = (() => {
    try {
      const m = /\(([^)]+)\)/.exec(product?.title || '')
      if (!m) return []
      const raw = m[1]
      return raw.split('+').map((s: string) => s.trim())
    } catch { return [] }
  })()

  // Lightbox state
  const [isLightboxOpen, setLightboxOpen] = useState(false)
  // Track if we pushed a history state for the lightbox so back gestures close it first
  const lbPushedStateRef = useRef(false)
  const [lbIndex, setLbIndex] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const panStartRef = useRef({ x: 0, y: 0 })
  const lastTranslateRef = useRef({ x: 0, y: 0 })
  // Lightbox swipe gesture (for zoom === 1): provide a smooth drag feedback
  const [swipeX, setSwipeX] = useState(0)
  const [isSwipeDragging, setIsSwipeDragging] = useState(false)
  const [isSwipeAnimating, setIsSwipeAnimating] = useState(false)
  const swipeStartRef = useRef(0)
  const swipeStartYRef = useRef(0)
  // Buy Now is rendered inline under "Included in the box" for all breakpoints
  // Double‑tap detection for mobile (fallback when onDoubleClick isn't fired)
  const lastTapTsRef = useRef(0)
  const lastTapXRef = useRef(0)
  const lbRef = useRef<HTMLDivElement | null>(null)
  const lbViewportRef = useRef<HTMLDivElement | null>(null)
  const [lbW, setLbW] = useState(0)
  useEffect(() => {
    const update = () => setLbW(lbViewportRef.current?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0))
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  

  useEffect(() => {
    if (!isLightboxOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') navigateLightbox(1)
      if (e.key === 'ArrowLeft') navigateLightbox(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isLightboxOpen, lbIndex, gallery.length])

  // Prevent background scroll when lightbox is open (desktop + mobile)
  useEffect(() => {
    if (!isLightboxOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const preventTouch = (e: TouchEvent) => { e.preventDefault() }
    document.addEventListener('touchmove', preventTouch, { passive: false })
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('touchmove', preventTouch as any)
    }
  }, [isLightboxOpen])

  // No sticky CTA logic (inline only)

  // No sticky CTAs; inline only

  function openLightboxAt(i: number) {
    setLbIndex(i)
    setZoom(1)
    setTranslate({ x: 0, y: 0 })
    setLightboxOpen(true)
    try { history.pushState({ modal: 'lightbox' }, '') ; lbPushedStateRef.current = true } catch {}
  }
  function closeLightbox() {
    try {
      if (lbPushedStateRef.current) {
        lbPushedStateRef.current = false
        // Pop the synthetic state first so the page itself does not navigate back
        history.back()
      }
    } catch {}
    try { (window as any).__overlayJustClosed = Date.now() } catch {}
    setLightboxOpen(false)
  }
  function navigateLightbox(direction: 1 | -1) {
    if (!gallery.length) return
    setLbIndex(prev => (prev + direction + gallery.length) % gallery.length)
    // reset pan/zoom
    setZoom(1)
    setTranslate({ x: 0, y: 0 })
    lastTranslateRef.current = { x: 0, y: 0 }
    setIsPanning(false)
  }

  // Update document title with variant color (client-side UX only)
  useEffect(() => {
    try {
      const label = activeVariant?.label ? String(activeVariant.label) : ''
      // Title: reuse shared helper to avoid duplicate size insertion
      const fullTitle = titleWithVariant(product, label)
      document.title = `${fullTitle} | Saffron Leo`

      // Meta description
      const baseDesc = product?.seo?.metaDescription || buildProductMetaDescription(product) || ''
      const withColor = label ? `${baseDesc} Colour: ${label}.` : baseDesc
      const sizeText = formatProductSize(product)
      const withSize = sizeText ? `${withColor} Size: ${sizeText}.` : withColor
      const desc = withSize.length > 165 ? `${withSize.slice(0, 162)}...` : withSize
      let tag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
      if (!tag) {
        tag = document.createElement('meta') as HTMLMetaElement
        tag.setAttribute('name', 'description')
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', desc)
    } catch {}
  }, [product, product?.seo?.metaDescription, product.title, activeVariant?.label, dims])

  // Intercept browser back/edge-swipe while lightbox is open
  useEffect(() => {
    const onPop = () => {
      if (isLightboxOpen) {
        lbPushedStateRef.current = false
        setLightboxOpen(false)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [isLightboxOpen])

  // Meta Pixel: product view for personalization
  useEffect(() => {
    try {
      const fbq = (window as any).fbq
      if (typeof fbq !== 'function') return
      const price = activeSalePrice ?? activeListPrice ?? product?.price
      const value = typeof price === 'number' ? price : undefined
      fbq('track', 'ViewContent', {
        content_type: 'product',
        content_ids: [product?.sku || product?.slug].filter(Boolean),
        content_name: product?.title || undefined,
        content_category: product?.category || undefined,
        value,
        currency: product?.pricing?.currency || 'INR',
      })
    } catch {}
  }, [product?.id, product?.slug, product?.sku, product?.salePrice, product?.price, product?.listPrice, product?.category, product?.pricing?.currency, activeSalePrice, activeListPrice])

  function trackAmazonClick() {
    try {
      const fbq = (window as any).fbq
      if (typeof fbq !== 'function') return
      const price = activeSalePrice ?? activeListPrice ?? product?.price
      const value = typeof price === 'number' ? price : undefined
      const contentId = (activeVariant as any)?.sku || product?.sku || product?.slug
      fbq('track', 'InitiateCheckout', {
        content_type: 'product',
        content_ids: [contentId].filter(Boolean),
        content_name: product?.title || undefined,
        content_category: product?.category || undefined,
        value,
        currency: product?.pricing?.currency || 'INR',
      })
    } catch {}
  }

  // Helper: is a point within the active image bounds?
  function isPointInsideActiveImage(x: number, y: number) {
    try {
      const img = lbViewportRef.current?.querySelector(`img[data-lb-index='${lbIndex}']`) as HTMLElement | null
      if (!img) return false
      const r = img.getBoundingClientRect()
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
    } catch {
      return false
    }
  }

  // Prefer FAQs from data; fallback to a richer generic set (6+)
  const faqs: { q: string; a: string }[] = useMemo(() => {
    const list = (product as any)?.faqs
    if (Array.isArray(list) && list.length) return list as any
    return [
      { q: 'Is this mat anti‑skid?', a: 'Yes. The TPR/non‑slip backing helps the mat stay put on tiles and wood.' },
      { q: 'Is it machine washable?', a: 'Yes. Wash cold on gentle and air dry or tumble dry low. Colours stay true.' },
      { q: 'Where can I use it?', a: 'Ideal beside the bed, hallways and doorways. Floor mats work well at bathroom exits and entrances.' },
      { q: 'Does it shed or leave lint?', a: 'The microfiber pile is tightly woven to minimize shedding. Shake occasionally or vacuum lightly to refresh.' },
      { q: 'How thick is it?', a: 'Approx. 1.5 cm with cushioned microfiber for a plush feel underfoot.' },
      { q: 'Can I use it on marble/tiles/wood?', a: 'Yes. The anti‑skid base grips smooth floors. Ensure the surface is dry and dust‑free for best hold.' },
      { q: 'Is the colour fast after washing?', a: 'Yes. Use mild detergent, wash separately in cold water and avoid bleach to keep colours vibrant.' },
      { q: 'Indoor or outdoor use?', a: 'Primarily indoor. Brief outdoor use is fine, but keep away from prolonged moisture and direct sunlight.' },
    ]
  }, [product])

  const couponsEnabled = (site as any)?.coupons?.enabled === true
  return (
    <>
      <section className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
        <div className="space-y-4">
          <div
            ref={heroRef}
            role="region"
            aria-roledescription="carousel"
            aria-label="Product images"
            className="relative aspect-[1/1] md:aspect-[4/3] overflow-hidden border select-none"
          >
            {/* Slides container */}
            <div
              key={activeVariant?.id || 'base'}
              className="flex h-full w-full"
              style={{
                transform: `translateX(${-(heroIndex * (slideW || 1)) + dragX}px)`,
                transition: animating && !isDragging ? 'transform 250ms ease-out' : 'none',
                willChange: 'transform',
              }}
              onClick={(e) => {
                if (didDragRef.current) { didDragRef.current = false; return }
                try {
                  const t = (window as any).__overlayJustClosed as number | undefined
                  if (t && Date.now() - t < 350) return
                } catch {}
                openLightboxAt(heroIndex)
              }}
              onTouchStart={(e) => {
                try {
                  setAnimating(false)
                  setIsDragging(true)
                  startXRef.current = e.touches?.[0]?.clientX || 0
                  setDragX(0)
                } catch {}
              }}
              onTouchMove={(e) => {
                if (!isDragging) return
                try {
                  const x = e.touches?.[0]?.clientX || 0
                  const dx = x - startXRef.current
                  if (Math.abs(dx) > 8) didDragRef.current = true
                  setDragX(dx)
                } catch {}
              }}
              onTouchEnd={(e) => {
                setIsDragging(false)
                setAnimating(true)
                const w = slideW || heroRef.current?.clientWidth || 1
                const threshold = Math.max(60, w * 0.18)
                const dx = dragX
                if (Math.abs(dx) > threshold && gallery.length > 1) {
                  const dir = dx < 0 ? 1 : -1
                  setHeroIndex(prev => (prev + dir + gallery.length) % gallery.length)
                }
                setDragX(0)
                // Reset drag marker after click cycle
                setTimeout(() => { didDragRef.current = false }, 0)
              }}
            >
              {gallery.map((g, i) => (
                <div key={g.src || i} className="h-full w-full shrink-0 grow-0 basis-full">
                  <div className={`relative h-full w-full ${isDragging ? 'transition-none' : ''}`}>
                    <Image
                      src={g.src}
                      alt={`${heroAlt} ${i+1}`}
                      fill
                      className="object-cover"
                      priority={i === heroIndex}
                      sizes="(min-width: 1024px) 55vw, 100vw"
                      draggable={false as any}
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile-only navigation arrows */}
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  className="sm:hidden absolute left-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-px rounded-none text-white w-auto h-auto"
                  style={{ background: '#00000026' }}
                  onClick={(e) => { e.stopPropagation(); setAnimating(true); setHeroIndex((i)=> (i - 1 + gallery.length) % gallery.length) }}
                >
                  <img src="/assets/brand/icons/chevron-left.svg" alt="Previous image" className="h-5 w-5" width="20" height="20" />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  className="sm:hidden absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-px rounded-none text-white w-auto h-auto"
                  style={{ background: '#00000026' }}
                  onClick={(e) => { e.stopPropagation(); setAnimating(true); setHeroIndex((i)=> (i + 1) % gallery.length) }}
                >
                  <img src="/assets/brand/icons/chevron-right.svg" alt="Next image" className="h-5 w-5" width="20" height="20" />
                </button>
                {/* Dots indicator */}
                <div className="sm:hidden absolute bottom-2 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-2">
                    {gallery.map((_, i) => (
                      <button key={i} type="button" aria-label={`Go to image ${i+1}`} onClick={(e)=> { e.stopPropagation(); setHeroIndex(i) }}
                        className={`h-2.5 w-2.5 rounded-full ${i===heroIndex ? 'bg-white' : 'bg-white/50'}`} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="hidden md:grid grid-cols-4 gap-3">
              {gallery.map((g, i) => (
                <button key={g.src} type="button" onClick={() => setHeroIndex(i)} aria-label={`View image ${i+1}`}
                  className={`relative aspect-[4/3] overflow-hidden border transition ${heroIndex===i?'border-brand ring-2 ring-brand/30':'border-gray-200 hover:border-gray-400'}`}>
                  <img src={g.src} alt={`${product.title}${activeVariant?.label ? ' - ' + activeVariant.label : ''} image ${i+1}`} className="h-full w-full object-cover" loading="lazy" decoding="async" width="300" height="225" />
                </button>
              ))}
            </div>
          )}
        </div>

        <article className="space-y-6 border bg-white px-6 py-6 shadow-sm">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold leading-tight">
              {displayHeading}
              {activeVariant?.label ? (
                <span className="text-base font-normal text-gray-600"> — {activeVariant.label}</span>
              ) : null}
            </h1>
            {/* Announce color change for assistive tech */}
            <span className="sr-only" aria-live="polite">
              {activeVariant?.label ? `Selected colour: ${activeVariant.label}` : ''}
            </span>
            {/* Hide category under title per request */}
            {typeof (activeSalePrice ?? activeListPrice ?? product.price) === 'number' && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-3xl font-semibold text-gray-900 flex items-baseline gap-3">
                  <span>₹{(activeSalePrice ?? activeListPrice ?? product.price).toLocaleString('en-IN')}</span>
                  {typeof activeSalePrice === 'number' && typeof activeListPrice === 'number' && activeSalePrice < activeListPrice && (
                    <span className="text-base font-normal text-gray-500 line-through">₹{activeListPrice.toLocaleString('en-IN')}</span>
                  )}
                </div>
                {(() => {
                  const href = (product as any)?.amazonUrl || (product as any)?.amazon?.url || null
                  if (href) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden"
                        onClick={trackAmazonClick}
                      >
                        Buy on Amazon
                        <img src="/assets/brand/icons/amazon.svg" alt="Amazon" className="h-4 w-4" loading="lazy" decoding="async" width="16" height="16" />
                      </a>
                    )
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new Event('open-coming-soon'))}
                      className="hidden"
                    >
                      Coming soon
                    </button>
                  )
                })()}
              </div>
            )}
          </div>

          {/* Coupon block moved to after Buy on Amazon button */}

          {!!variantOptions.length && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-gray-900">{product.variants?.label ?? 'Options'}</h2>
              <div className="flex flex-wrap gap-2">
                {variantOptions.map((o: any) => {
                  const selected = o.id === (activeVariant?.id || activeVariantId)
                  return (
                    <button key={o.id} type="button" onClick={() => setActiveVariantId(o.id)}
                      className={`flex items-center gap-2 rounded border px-3 py-1.5 text-xs transition ${selected?'border-black bg-black text-white shadow-sm':'border-gray-200 hover:border-black hover:text-black'}`}>
                      {o.swatch ? <span className="inline-block h-4 w-4 rounded border border-white shadow" style={{backgroundColor:o.swatch}} aria-hidden /> : (
                        <img src={o.image || product.cover} alt={`${product.title} - ${o.label}`} className="h-6 w-6 rounded object-cover" loading="lazy" decoding="async" width="24" height="24" />
                      )}
                      <span>{o.label}</span>
                    </button>
                  )
                })}
              </div>
            </section>
          )}

          {(() => {
            const entries: Array<[string, any]> = product?.specifications ? Object.entries(product.specifications) : []
            const existing = new Set(entries.map(([k]) => String(k).toLowerCase()))
            type Row = { key: string; label: string; value: any }
            const rows: Row[] = []
            for (const [k, v] of entries) {
              if (!v) continue
              const kLower = String(k).toLowerCase()
              // Hide pattern, colors and backing (covered by material/UX above)
              if (kLower === 'pattern' || kLower === 'colors' || kLower === 'colour' || kLower === 'color' || kLower === 'backing' || kLower === 'origin') continue
              const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())
              rows.push({ key: k, label, value: Array.isArray(v) ? v.join(', ') : v })
            }
            // Merge former Highlights into Specifications without duplicates
            if (dims && !existing.has('dimensions') && !existing.has('runner') && !existing.has('bathmat') && !existing.has('size')) {
              rows.push({ key: 'size', label: 'Size', value: dims })
            }
            if (product.material && !existing.has('material')) {
              rows.push({ key: 'material', label: 'Material', value: product.material })
            }
            if (product.thickness && !existing.has('thickness')) {
              rows.push({ key: 'thickness', label: 'Thickness', value: product.thickness })
            }
            // Do not add backing row, it's redundant with material UX
            if (!rows.length) return null
            return (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Specifications</h2>
                <dl className="mt-3 grid gap-2 text-sm text-gray-700">
                  {rows.map(r => (
                    <div key={r.key} className="flex items-start justify-between gap-3">
                      <dt className="text-gray-500">{r.label}</dt>
                      <dd className="text-right font-medium text-gray-900">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )
          })()}

          {Array.isArray(product.includedComponents) && product.includedComponents.length ? (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Included in the box</h2>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {product.includedComponents.map((c: string, i:number) => {
                  let size: string | '' = ''
                  if (/runner/i.test(c)) {
                    size = runnerSize || (titleSizeParts[0] || '') || dims || ''
                  } else if (/(bath|door|floor)\s*mat/i.test(c)) {
                    size = bathMatSize || (titleSizeParts[1] || titleSizeParts[0] || '') || dims || ''
                  } else {
                    size = dims || (titleSizeParts[0] || '')
                  }
                  const label = size ? `${c} (${size})` : c
                  return (<li key={i}>{label}</li>)
                })}
              </ul>
              {/* Buy now below Included in the box (desktop only; hidden on mobile) */}
              <div className="mt-4 hidden md:block">
                {(() => {
                  const variantComingSoon = Boolean((activeVariant as any)?.comingSoon)
                  const vHref = (activeVariant as any)?.amazonUrl || (activeVariant as any)?.amazon?.url || null
                  const href = variantComingSoon ? null : (vHref || (product as any)?.amazonUrl || (product as any)?.amazon?.url || null)
                  if (href) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full md:w-auto items-center justify-center gap-2 rounded border border-brand bg-white px-5 py-2 text-base font-medium text-black transition-colors duration-200 hover:bg-brand hover:text-white whitespace-nowrap"
                        onClick={trackAmazonClick}
                      >
                        Buy on Amazon
                        <img src="/assets/brand/icons/amazon.svg" alt="Amazon" className="h-4 w-4" loading="lazy" decoding="async" width="16" height="16" />
                      </a>
                    )
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new Event('open-coming-soon'))}
                      className="inline-flex w-full md:w-auto items-center justify-center rounded border px-5 py-2 text-base font-medium hover:bg-gray-50 whitespace-nowrap"
                    >
                      Coming soon
                    </button>
                  )
                })()}
                <div className="mt-2 text-xs text-gray-600">
                  ✅ Free delivery • ✅ Easy returns • ✅ Secure payment
                </div>
              </div>
            </section>
          ) : null}

          {/* Highlights removed; merged into Specifications */}

          {couponsEnabled && product?.pricing?.coupon?.code && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900 text-sm space-y-2">
              {product?.pricing?.coupon?.note && (
                <div className="leading-snug">{product.pricing.coupon.note}</div>
              )}
              <div className="flex items-center justify-between">
                <span className="font-mono bg-white/80 rounded px-2 py-0.5">{product.pricing.coupon.code}</span>
                <button
                  type="button"
                  onClick={async () => { try { await navigator.clipboard.writeText(product.pricing.coupon.code) } catch (_) {} }}
                  className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-xs hover:bg-white/60 whitespace-nowrap"
                  aria-label="Copy coupon code"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
            </div>
          )}
        </article>
      </section>

      {/* Product overview (full-width) */}
      {product?.description && (
        <section className="mt-8 border bg-white px-6 py-6 shadow-sm">
          <h2 className="text-lg font-semibold">Product overview</h2>
          <div className="product-content mt-2 leading-relaxed text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: product.description }} />
        </section>
      )}

      {/* Key features + Care instructions grid */}
      {(product?.bulletPoints?.length || (Array.isArray(product?.careInstructions) && product.careInstructions.length)) ? (
        <section className="mt-8 grid gap-6 md:grid-cols-2">
          {product?.bulletPoints?.length ? (
            <div className="border bg-white px-6 py-6 shadow-sm">
              <h2 className="text-lg font-semibold">Key features</h2>
              <ul className="mt-3 list-disc space-y-1 pl-6 text-sm text-gray-700">
                {product.bulletPoints.slice(0,8).map((b: string, i:number) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          ) : null}
          {Array.isArray(product?.careInstructions) && product.careInstructions.length ? (
            <div className="border bg-white px-6 py-6 shadow-sm">
              <h2 className="text-lg font-semibold">Care instructions</h2>
              <ul className="mt-3 list-disc space-y-1 pl-6 text-sm text-gray-700">
                {product.careInstructions.map((c: string, i:number) => (<li key={i}>{c}</li>))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* A+ Content */}
      {product?.aPlusContent && (
        <section className="mt-8 space-y-6">
          <div>
            <h2 className="text-xl font-semibold">Crafted details & aesthetics</h2>
            <p className="text-sm text-gray-500">A closer look at textures, finishing and material choices that define our pieces.</p>
          </div>
          <APlusContent content={product.aPlusContent} productTitle={product.title} />
        </section>
      )}

      {/* FAQ moved below suggestions */}

      {Array.isArray(suggestions) && suggestions.length ? (
        <section className="mt-8 space-y-1">
          <div>
            <h2 className="text-xl font-semibold">You might also like</h2>
            <p className="text-sm text-gray-500">Curated from the same collection to complement your pick.</p>
          </div>
          {/* Mobile: horizontal scroll list */}
          <div className="mt-3 -mx-4 px-4 overflow-x-auto overflow-y-hidden sm:hidden snap-x snap-mandatory scroll-px-4" style={{ WebkitOverflowScrolling: 'touch' as any }}>
            <div className="flex w-max gap-4">
              {suggestions.map((r: any) => (
                <div key={r.id} className="snap-start shrink-0 w-64">
                  <PdpCard p={r} />
                </div>
              ))}
            </div>
          </div>
          {/* Tablet/Desktop: grid */}
          <div className="mt-3 hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-5">
            {suggestions.map((r: any) => <PdpCard key={r.id} p={r} />)}
          </div>
        </section>
      ) : null}

      {/* PDP FAQ (moved after suggestions) */}
      {faqs.length > 0 && (
        <section className="mt-8 space-y-3 border bg-white px-6 py-6 shadow-sm">
          <h2 className="text-lg font-semibold">FAQs</h2>
          <div className="space-y-2 text-sm text-gray-700">
            {faqs.map((f, i) => (
              <details key={i} className="rounded border p-3">
                <summary className="cursor-pointer font-medium">{f.q}</summary>
                <p className="mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox overlay */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 text-white !m-0 touch-none"
          onPointerDown={(e) => { if (e.target === e.currentTarget) { try { e.preventDefault(); e.stopPropagation() } catch {}; closeLightbox() } }}
          onClick={(e) => { try { e.preventDefault(); e.stopPropagation() } catch {} }}
        >
          <button
            type="button"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded bg-white/10 hover:bg-white/20 z-10"
            aria-label="Close gallery"
            onClick={closeLightbox}
          >
            ✕
          </button>
          {gallery.length > 1 && (
            <>
              {/* Mobile arrows (match main slider) */}
              <button
                type="button"
                aria-label="Previous image"
                className="sm:hidden absolute left-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-px rounded-none text-white w-auto h-auto z-10"
                style={{ background: '#00000026' }}
                onClick={() => navigateLightbox(-1)}
              >
                <img src="/assets/brand/icons/chevron-left.svg" alt="Previous image" className="h-5 w-5" width="20" height="20" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                className="sm:hidden absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-px rounded-none text-white w-auto h-auto z-10"
                style={{ background: '#00000026' }}
                onClick={() => navigateLightbox(1)}
              >
                <img src="/assets/brand/icons/chevron-right.svg" alt="Next image" className="h-5 w-5" width="20" height="20" />
              </button>
              {/* Desktop/tablet arrows */}
              <button
                type="button"
                aria-label="Previous image"
                className="hidden sm:inline-flex absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded bg-black/40 hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white/40 z-10"
                onClick={() => navigateLightbox(-1)}
              >
                <img src="/assets/brand/icons/chevron-left.svg" alt="Previous image" className="h-6 w-6" width="24" height="24" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                className="hidden sm:inline-flex absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded bg-black/40 hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white/40 z-10"
                onClick={() => navigateLightbox(1)}
              >
                <img src="/assets/brand/icons/chevron-right.svg" alt="Next image" className="h-6 w-6" width="24" height="24" />
              </button>
              {/* Thumbnail strip at bottom */}
              <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/50 px-3 py-2 overflow-x-auto">
                <div className="flex items-center gap-2">
                  {gallery.map((g, i) => (
                    <button key={g.src + i} type="button" aria-label={`Go to image ${i+1}`} onClick={() => setLbIndex(i)}
                      className={`h-14 w-14 flex-shrink-0 overflow-hidden border ${i===lbIndex ? 'border-white ring-2 ring-white/60' : 'border-white/30 hover:border-white/60'}`}>
                      <img src={g.src} alt={g.caption || product.title} className="h-full w-full object-cover" loading="lazy" decoding="async" width="56" height="56" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div
            ref={lbViewportRef}
            className="relative h-full w-full overflow-hidden select-none"
            onTouchStart={(e) => {
              try {
                const x = e.touches?.[0]?.clientX || 0
                const y = e.touches?.[0]?.clientY || 0
                // If the touch starts outside the active image area, treat as backdrop interaction: close
                if (!isPointInsideActiveImage(x, y)) { closeLightbox(); return }
                ;(e.currentTarget as any)._sx = x
                swipeStartYRef.current = y
                if (zoom === 1) {
                  setIsSwipeDragging(true)
                  setIsSwipeAnimating(false)
                  swipeStartRef.current = x
                  setSwipeX(0)
                } else {
                  // Begin touch panning when zoomed in
                  setIsPanning(true)
                  panStartRef.current = { x, y }
                  lastTranslateRef.current = { ...translate }
                  e.preventDefault()
                }
              } catch {}
            }}
            onTouchEnd={(e) => {
              try {
                const endX = (e.changedTouches?.[0]?.clientX || 0)
                // Double‑tap zoom toggle (works even on mobile where dblclick isn't fired)
                const now = Date.now()
                const dt = now - (lastTapTsRef.current || 0)
                const dxTap = Math.abs(endX - (lastTapXRef.current || 0))
                if (dt < 300 && dxTap < 20) {
                  const next = zoom === 1 ? 2 : 1
                  setZoom(next)
                  if (next === 1) {
                    setTranslate({ x: 0, y: 0 })
                    lastTranslateRef.current = { x: 0, y: 0 }
                  }
                  // reset swipe state
                  setIsSwipeDragging(false)
                  setIsSwipeAnimating(false)
                  setSwipeX(0)
                  lastTapTsRef.current = 0
                  lastTapXRef.current = 0
                  return
                }
                lastTapTsRef.current = now
                lastTapXRef.current = endX

                if (zoom !== 1) {
                  // End panning
                  setIsPanning(false)
                  return
                }
                const startX = swipeStartRef.current || (e.currentTarget as any)._sx || 0
                const dx = endX - startX
                setIsSwipeDragging(false)
                const threshold = Math.max(60, (lbW || 300) * 0.18)
                if (Math.abs(dx) > threshold && gallery.length > 1) {
                  setIsSwipeAnimating(true)
                  // change index; container will animate to new position
                  navigateLightbox(dx < 0 ? 1 : -1)
                  setSwipeX(0)
                  setTimeout(() => setIsSwipeAnimating(false), 250)
                } else {
                  // animate back to center
                  setIsSwipeAnimating(true)
                  setSwipeX(0)
                  setTimeout(() => setIsSwipeAnimating(false), 220)
                }
              } catch {}
            }}
            onTouchMove={(e) => {
              if (zoom !== 1) {
                if (!isPanning) return
                try {
                  const x = e.touches?.[0]?.clientX || 0
                  const y = e.touches?.[0]?.clientY || 0
                  const dx = x - panStartRef.current.x
                  const dy = y - panStartRef.current.y
                  setTranslate({ x: lastTranslateRef.current.x + dx, y: lastTranslateRef.current.y + dy })
                  e.preventDefault()
                } catch {}
                return
              }
              if (!isSwipeDragging) return
              try {
                const x = e.touches?.[0]?.clientX || 0
                const y = e.touches?.[0]?.clientY || 0
                const dx = x - swipeStartRef.current
                const dy = y - (swipeStartYRef.current || 0)
                e.preventDefault()
                // Detect vertical swipe-down to close when zoom === 1
                const vh = (lbViewportRef.current?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 600))
                const yThreshold = Math.max(80, vh * 0.18)
                if (dy > yThreshold && Math.abs(dy) > Math.abs(dx) + 20) {
                  closeLightbox()
                  return
                }
                setSwipeX(dx)
              } catch {}
            }}
            onWheel={(e) => {
              e.preventDefault()
              const delta = -e.deltaY
              const step = delta > 0 ? 0.1 : -0.1
              const next = Math.max(1, Math.min(4, Number((zoom + step).toFixed(2))))
              if (next === zoom) return
              setZoom(next)
              if (next === 1) {
                setTranslate({ x: 0, y: 0 })
                lastTranslateRef.current = { x: 0, y: 0 }
              }
            }}
            onMouseDown={(e) => {
              if (zoom === 1) return
              setIsPanning(true)
              panStartRef.current = { x: e.clientX, y: e.clientY }
              lastTranslateRef.current = { ...translate }
            }}
            onMouseUp={() => setIsPanning(false)}
            onMouseLeave={() => setIsPanning(false)}
            onMouseMove={(e) => {
              if (!isPanning) return
              const dx = e.clientX - panStartRef.current.x
              const dy = e.clientY - panStartRef.current.y
              setTranslate({ x: lastTranslateRef.current.x + dx, y: lastTranslateRef.current.y + dy })
            }}
            onPointerDown={(e) => {
              // Close when tapping/clicking outside the active image bounds
              try {
                const img = lbViewportRef.current?.querySelector(`img[data-lb-index='${lbIndex}']`) as HTMLElement | null
                if (!img) { try { e.preventDefault(); e.stopPropagation() } catch {}; closeLightbox(); return }
                const r = img.getBoundingClientRect()
                const x = (e as any).clientX ?? 0
                const y = (e as any).clientY ?? 0
                const inside = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
                if (!inside) { try { e.preventDefault(); e.stopPropagation() } catch {}; closeLightbox() }
              } catch { try { e.preventDefault(); e.stopPropagation() } catch {}; closeLightbox() }
            }}
          >
            {/* Slides inside the lightbox */}
            <div
              ref={lbRef}
              className="flex h-full w-full"
              style={{
                transform: `translateX(${-(lbIndex * (lbW || 1)) + (zoom === 1 ? swipeX : 0)}px)`,
                transition: (isSwipeDragging || isPanning) ? 'none' : (isSwipeAnimating ? 'transform 250ms ease-out' : 'transform 200ms ease-in-out'),
                willChange: 'transform'
              }}
            >
              {gallery.map((g, i) => (
                <div key={g.src || i} className="h-full w-full shrink-0 grow-0 basis-full flex items-center justify-center">
                  <img
                    src={g.src}
                    alt={`${heroAlt} ${i+1}`}
                    className={`max-h-[82vh] w-auto object-contain ${zoom === 1 ? 'cursor-zoom-in' : isPanning ? 'cursor-grabbing' : 'cursor-grab'} select-none`}
                    style={{
                      transform: `translate(${i===lbIndex ? translate.x : 0}px, ${i===lbIndex ? translate.y : 0}px) scale(${i===lbIndex ? zoom : 1})`,
                      transition: isPanning ? 'none' : 'transform 200ms ease-in-out'
                    }}
                    data-lb-index={i}
                    onDoubleClick={() => {
                      const next = zoom === 1 ? 2 : 1
                      setZoom(next)
                      if (next === 1) {
                        setTranslate({ x: 0, y: 0 })
                        lastTranslateRef.current = { x: 0, y: 0 }
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Removed duplicate related section to avoid two "You might also like" blocks */}

      {/* Spacer so content above isn’t obscured by sticky bar on mobile */}
      <div className="h-24 md:hidden" aria-hidden="true" />

      {/* Mobile sticky Buy now bar (full width, padded, z-index ~4) */}
      <div className="fixed inset-x-0 bottom-0 z-[4] md:hidden">
        <div className="w-full bg-white px-8 py-4">
          <div className="mx-auto max-w-6xl">
            {(() => {
              const variantComingSoon = Boolean((activeVariant as any)?.comingSoon)
              const vHref = (activeVariant as any)?.amazonUrl || (activeVariant as any)?.amazon?.url || null
              const href = variantComingSoon ? null : (vHref || (product as any)?.amazonUrl || (product as any)?.amazon?.url || null)
              if (href) {
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded border border-brand bg-white px-5 py-2 text-base font-medium text-black transition-colors duration-200 hover:bg-brand hover:text-white"
                    onClick={trackAmazonClick}
                  >
                    Buy on Amazon
                    <img src="/assets/brand/icons/amazon.svg" alt="Amazon" className="h-5 w-5" loading="lazy" decoding="async" width="20" height="20" />
                  </a>
                )
              }
              return (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('open-coming-soon'))}
                  className="inline-flex w-full items-center justify-center rounded border px-5 py-2 text-base font-medium hover:bg-gray-50"
                >
                  Coming soon
                </button>
              )
            })()}
            <div className="mt-2 text-center text-xs text-gray-600">
              ✅ Free delivery • ✅ Easy returns • ✅ Secure payment
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function PdpCard({ p }: { p: any }) {
  const href = `/category/${categorySlugForProduct(p)}/${p.slug}/`
  const price = (p.salePrice ?? p.price) || 0
  const showStrike = typeof p.salePrice === 'number' && typeof p.listPrice === 'number' && p.salePrice < p.listPrice
  const colours = p?.variants?.options?.length || 0
  return (
    <Link href={href} className="block group overflow-hidden border bg-white hover:shadow-md transition">
      <div className="aspect-[4/3] overflow-hidden">
        {(() => { const def = (p?.variants?.options || []).find((o:any)=> o.id === (p?.variants?.defaultId || '')); const c = def?.label; const alt = c ? `${p.title} - ${c}` : p.title; return (
          <img src={p.cover} alt={alt} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" decoding="async" width="600" height="450" />
        )})()}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-snug line-clamp-2 text-sm md:text-base">{(() => { const c = defaultVariantColor(p); return titleWithVariant(p, c) })()}</h3>
        </div>
        {typeof p.rating === 'number' && (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span className="inline-flex items-center gap-0.5" aria-label={`Rated ${p.rating} out of 5`}>
              {Array.from({ length: 5 }).map((_, idx) => {
                const fill = Math.max(0, Math.min(1, p.rating - idx))
                const fillPct = Math.round(fill * 100)
                return (
                  <span key={idx} className="relative inline-block h-3 w-3 text-gray-300">
                    <Star size={12} className="h-3 w-3 text-gray-300" />
                    <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                      <Star size={12} className="h-3 w-3 fill-current text-amber-500" />
                    </span>
                  </span>
                )
              })}
            </span>
          </div>
        )}
        {/* Category + colors: single line on all viewports */}
        <div className="text-sm text-gray-600">
          {colours > 1 ? `${p.category} - ${colours} ${colours === 1 ? 'color' : 'colors'}` : p.category}
        </div>
        <div className="text-base font-semibold flex items-baseline gap-2">
          <span>₹{price.toLocaleString('en-IN')}</span>
          {showStrike && <span className="text-xs font-normal text-gray-500 line-through">₹{(p.listPrice||0).toLocaleString('en-IN')}</span>}
        </div>
        {/* Removed keyword tags for consistent card height in suggestions */}
        {/* Removed secondary "colors available" line for desktop */}
      </div>
    </Link>
  )
}
